import { useState, useCallback, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import {
  Upload,
  FileText,
  FileSpreadsheet,
  FileImage,
  File,
  Trash2,
  RefreshCw,
  Filter,
  Search,
  AlertCircle,
  CheckCircle,
  Clock,
  X,
  Video,
  Calendar,
  Users,
} from 'lucide-react'
import * as api from '../services/api'
import type { Document, DocumentCategory, TeamsMeeting } from '../types'

const categoryOptions: { value: DocumentCategory; label: string; description: string }[] = [
  { value: 'wbs', label: 'WBS', description: 'Work Breakdown Structure' },
  { value: 'sow', label: 'SOW', description: 'Statement of Work' },
  { value: 'transcript', label: 'Transcript', description: 'Teams meeting transcripts' },
  { value: 'requirements', label: 'Requirements', description: 'Requirements documents' },
  {
    value: 'azure_consumption',
    label: 'Azure Consumption',
    description: 'Azure cost estimates and consumption impact',
  },
  { value: 'image', label: 'Image', description: 'Images and diagrams' },
  { value: 'other', label: 'Other', description: 'Other supporting documents' },
]

const fileTypeIcons: Record<string, typeof FileText> = {
  'application/pdf': FileText,
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': FileText,
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': FileSpreadsheet,
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': FileText,
  'image/png': FileImage,
  'image/jpeg': FileImage,
  'text/plain': FileText,
  'text/vtt': FileText,
}

function getFileIcon(mimeType?: string) {
  if (!mimeType) return File
  return fileTypeIcons[mimeType] || File
}

function formatFileSize(bytes?: number): string {
  if (!bytes) return 'Unknown'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function getStatusBadge(status: string) {
  switch (status) {
    case 'processed':
      return (
        <span className="badge-green">
          <CheckCircle className="w-3 h-3" />
          Processed
        </span>
      )
    case 'processing':
      return (
        <span className="badge-blue">
          <RefreshCw className="w-3 h-3 animate-spin" />
          Processing
        </span>
      )
    case 'pending':
      return (
        <span className="badge-yellow">
          <Clock className="w-3 h-3" />
          Pending
        </span>
      )
    case 'error':
      return (
        <span className="badge-red">
          <AlertCircle className="w-3 h-3" />
          Error
        </span>
      )
    default:
      return <span className="badge-gray">{status}</span>
  }
}

function formatDateTime(dateString?: string): string {
  if (!dateString) return 'N/A'
  const date = new Date(dateString)
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

export default function Documents() {
  const { projectId } = useParams<{ projectId: string }>()
  const [documents, setDocuments] = useState<Document[]>([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [filterCategory, setFilterCategory] = useState<DocumentCategory | ''>('')
  const [searchQuery, setSearchQuery] = useState('')
  const [dragActive, setDragActive] = useState(false)

  // Upload modal state
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [uploadCategory, setUploadCategory] = useState<DocumentCategory>('other')

  // Teams import modal state
  const [showTeamsModal, setShowTeamsModal] = useState(false)
  const [teamsMeetings, setTeamsMeetings] = useState<TeamsMeeting[]>([])
  const [loadingMeetings, setLoadingMeetings] = useState(false)
  const [importingMeeting, setImportingMeeting] = useState<string | null>(null)

  const fetchDocuments = useCallback(async () => {
    if (!projectId) return
    setLoading(true)
    setError(null)
    try {
      const response = await api.getDocuments(projectId, filterCategory || undefined)
      setDocuments(response.data.documents || [])
    } catch (err) {
      console.error('Failed to fetch documents:', err)
      setError('Failed to load documents')
    } finally {
      setLoading(false)
    }
  }, [projectId, filterCategory])

  useEffect(() => {
    fetchDocuments()
  }, [fetchDocuments])

  const fetchTeamsMeetings = useCallback(async () => {
    setLoadingMeetings(true)
    setError(null)
    try {
      // Get meetings from the last 30 days
      const endDate = new Date()
      const startDate = new Date()
      startDate.setDate(startDate.getDate() - 30)

      const response = await api.getTeamsMeetings(
        startDate.toISOString(),
        endDate.toISOString(),
        50
      )
      setTeamsMeetings(response.data.meetings)
    } catch (err) {
      console.error('Failed to fetch Teams meetings:', err)
      setError('Failed to load Teams meetings. Ensure you have granted the required permissions.')
    } finally {
      setLoadingMeetings(false)
    }
  }, [])

  const handleOpenTeamsModal = () => {
    setShowTeamsModal(true)
    fetchTeamsMeetings()
  }

  const handleImportTeamsTranscript = async (meeting: TeamsMeeting) => {
    if (!projectId) return

    setImportingMeeting(meeting.id)
    setError(null)

    try {
      const response = await api.importTeamsTranscript(projectId, {
        meeting_id: meeting.id,
        join_url: meeting.join_url,
      })

      if (response.data.success) {
        setSuccessMessage(`Imported transcript: ${response.data.filename}`)
        setShowTeamsModal(false)
        await fetchDocuments()
      } else {
        setError(response.data.message)
      }
    } catch (err) {
      console.error('Failed to import Teams transcript:', err)
      setError('Failed to import transcript')
    } finally {
      setImportingMeeting(null)
    }
  }

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    const files = Array.from(e.dataTransfer.files)
    if (files.length > 0) {
      setSelectedFiles(files)
      setShowUploadModal(true)
    }
  }, [])

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    if (files.length > 0) {
      setSelectedFiles(files)
      setShowUploadModal(true)
    }
  }, [])

  const handleUpload = async () => {
    if (!projectId || selectedFiles.length === 0) return

    setUploading(true)
    setError(null)

    try {
      for (const file of selectedFiles) {
        await api.uploadDocument(projectId, file, uploadCategory)
      }
      setShowUploadModal(false)
      setSelectedFiles([])
      await fetchDocuments()
    } catch (err) {
      console.error('Failed to upload documents:', err)
      setError('Failed to upload one or more documents')
    } finally {
      setUploading(false)
    }
  }

  const handleDelete = async (documentId: string) => {
    if (!projectId) return
    if (!confirm('Are you sure you want to delete this document?')) return

    try {
      await api.deleteDocument(projectId, documentId)
      await fetchDocuments()
    } catch (err) {
      console.error('Failed to delete document:', err)
      setError('Failed to delete document')
    }
  }

  const handleReprocess = async (documentId: string) => {
    if (!projectId) return

    try {
      await api.reprocessDocument(projectId, documentId)
      await fetchDocuments()
    } catch (err) {
      console.error('Failed to reprocess document:', err)
      setError('Failed to reprocess document')
    }
  }

  const filteredDocuments = documents.filter((doc) => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      return (
        doc.original_filename.toLowerCase().includes(query) ||
        doc.doc_category.toLowerCase().includes(query)
      )
    }
    return true
  })

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">Documents</h1>
          <p className="text-slate-400 mt-1">Upload and manage project documents for AI analysis</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleOpenTeamsModal} className="btn-secondary">
            <Video className="w-4 h-4" />
            Import from Teams
          </button>
          <button onClick={() => setShowUploadModal(true)} className="btn-primary">
            <Upload className="w-4 h-4" />
            Upload Documents
          </button>
        </div>
      </div>

      {/* Success Alert */}
      {successMessage && (
        <div className="alert-success">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <div>
            <p className="font-medium">Success</p>
            <p className="text-sm">{successMessage}</p>
          </div>
          <button onClick={() => setSuccessMessage(null)} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Error Alert */}
      {error && (
        <div className="alert-error">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <div>
            <p className="font-medium">Error</p>
            <p className="text-sm">{error}</p>
          </div>
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input
            type="text"
            placeholder="Search documents..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input pl-10"
          />
        </div>
        <div className="relative">
          <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value as DocumentCategory | '')}
            className="select pl-10 w-48"
          >
            <option value="">All Categories</option>
            {categoryOptions.map((cat) => (
              <option key={cat.value} value={cat.value}>
                {cat.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Drop Zone */}
      <div
        className={`file-dropzone ${dragActive ? 'file-dropzone-active' : ''}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => document.getElementById('file-input')?.click()}
      >
        <input
          id="file-input"
          type="file"
          multiple
          className="hidden"
          onChange={handleFileSelect}
          accept=".pdf,.doc,.docx,.xlsx,.xls,.xlsm,.csv,.pptx,.ppt,.txt,.md,.vtt,.png,.jpg,.jpeg,.gif,.bmp,.webp"
        />
        <Upload className="w-12 h-12 text-slate-500 mx-auto mb-4" />
        <p className="text-slate-300 mb-2">Drag and drop files here, or click to browse</p>
        <p className="text-sm text-slate-500">Supports PDF, DOCX, XLSX, PPTX, TXT, VTT, PNG, JPG</p>
      </div>

      {/* Documents List */}
      {loading ? (
        <div className="card">
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-4">
                <div className="skeleton w-10 h-10 rounded-lg" />
                <div className="flex-1">
                  <div className="skeleton h-4 w-48 mb-2" />
                  <div className="skeleton h-3 w-32" />
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : filteredDocuments.length === 0 ? (
        <div className="card text-center py-12">
          <FileText className="w-16 h-16 text-slate-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-300 mb-2">No documents yet</h3>
          <p className="text-slate-500 mb-4">
            Upload WBS, SOW, transcripts, and other project documents to get started
          </p>
          <div className="flex items-center justify-center gap-2">
            <button onClick={handleOpenTeamsModal} className="btn-secondary">
              <Video className="w-4 h-4" />
              Import from Teams
            </button>
            <button onClick={() => setShowUploadModal(true)} className="btn-primary">
              <Upload className="w-4 h-4" />
              Upload Documents
            </button>
          </div>
        </div>
      ) : (
        <div className="card p-0">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Document</th>
                  <th>Category</th>
                  <th>Size</th>
                  <th>Status</th>
                  <th>Uploaded</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredDocuments.map((doc) => {
                  const FileIcon = getFileIcon(doc.file_type)
                  return (
                    <tr key={doc.id}>
                      <td>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-slate-700/50 rounded-lg flex items-center justify-center">
                            <FileIcon className="w-5 h-5 text-slate-400" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-200 truncate max-w-xs">
                              {doc.original_filename}
                            </p>
                            <p className="text-xs text-slate-500">{doc.file_type}</p>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="badge-blue">{doc.doc_category.toUpperCase()}</span>
                      </td>
                      <td className="text-slate-400">{formatFileSize(doc.file_size)}</td>
                      <td>{getStatusBadge(doc.status)}</td>
                      <td className="text-slate-400">
                        {new Date(doc.created_at).toLocaleDateString()}
                      </td>
                      <td>
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleReprocess(doc.id)}
                            className="btn-ghost btn-icon btn-sm"
                            title="Reprocess"
                          >
                            <RefreshCw className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(doc.id)}
                            className="btn-ghost btn-icon btn-sm text-red-400 hover:text-red-300"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="modal-overlay" onClick={() => !uploading && setShowUploadModal(false)}>
          <div className="modal max-w-md animate-scaleIn" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="text-lg font-semibold text-slate-100">Upload Documents</h3>
              <button
                onClick={() => !uploading && setShowUploadModal(false)}
                className="btn-ghost btn-icon btn-sm"
                disabled={uploading}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="modal-body space-y-4">
              <div>
                <label className="label">Selected Files</label>
                {selectedFiles.length === 0 ? (
                  <div
                    className={`file-dropzone cursor-pointer ${dragActive ? 'file-dropzone-active' : ''}`}
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                    onClick={() => document.getElementById('modal-file-input')?.click()}
                  >
                    <input
                      id="modal-file-input"
                      type="file"
                      multiple
                      className="hidden"
                      onChange={handleFileSelect}
                      accept=".pdf,.doc,.docx,.xlsx,.xls,.xlsm,.csv,.pptx,.ppt,.txt,.md,.vtt,.png,.jpg,.jpeg,.gif,.bmp,.webp"
                    />
                    <Upload className="w-8 h-8 text-slate-500 mx-auto mb-2" />
                    <p className="text-slate-300 text-sm">Click to browse or drag files here</p>
                    <p className="text-xs text-slate-500 mt-1">
                      PDF, DOCX, XLSX, PPTX, TXT, VTT, PNG, JPG
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {selectedFiles.map((file, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-3 p-2 bg-slate-800/50 rounded-lg"
                        >
                          <FileText className="w-4 h-4 text-slate-400" />
                          <span className="text-sm text-slate-300 truncate flex-1">
                            {file.name}
                          </span>
                          <span className="text-xs text-slate-500">
                            {formatFileSize(file.size)}
                          </span>
                          {!uploading && (
                            <button
                              onClick={() =>
                                setSelectedFiles(selectedFiles.filter((_, i) => i !== index))
                              }
                              className="text-slate-500 hover:text-slate-300"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                    {!uploading && (
                      <button
                        onClick={() => document.getElementById('modal-file-input-add')?.click()}
                        className="btn-ghost btn-sm mt-2 w-full"
                      >
                        <input
                          id="modal-file-input-add"
                          type="file"
                          multiple
                          className="hidden"
                          onChange={(e) => {
                            const newFiles = Array.from(e.target.files || [])
                            if (newFiles.length > 0) {
                              setSelectedFiles([...selectedFiles, ...newFiles])
                            }
                          }}
                          accept=".pdf,.doc,.docx,.xlsx,.xls,.xlsm,.csv,.pptx,.ppt,.txt,.md,.vtt,.png,.jpg,.jpeg,.gif,.bmp,.webp"
                        />
                        <Upload className="w-4 h-4" />
                        Add more files
                      </button>
                    )}
                  </>
                )}
              </div>
              <div>
                <label className="label">Document Category</label>
                <select
                  value={uploadCategory}
                  onChange={(e) => setUploadCategory(e.target.value as DocumentCategory)}
                  className="select"
                  disabled={uploading}
                >
                  {categoryOptions.map((cat) => (
                    <option key={cat.value} value={cat.value}>
                      {cat.label} - {cat.description}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button
                onClick={() => setShowUploadModal(false)}
                className="btn-secondary"
                disabled={uploading}
              >
                Cancel
              </button>
              <button
                onClick={handleUpload}
                className="btn-primary"
                disabled={uploading || selectedFiles.length === 0}
              >
                {uploading ? (
                  <>
                    <div className="spinner" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    Upload {selectedFiles.length} file{selectedFiles.length !== 1 ? 's' : ''}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Teams Import Modal */}
      {showTeamsModal && (
        <div
          className="modal-overlay"
          onClick={() => !importingMeeting && setShowTeamsModal(false)}
        >
          <div className="modal max-w-2xl animate-scaleIn" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                  <Video className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-100">Import Teams Transcript</h3>
                  <p className="text-sm text-slate-400">
                    Select a meeting to import its transcript
                  </p>
                </div>
              </div>
              <button
                onClick={() => !importingMeeting && setShowTeamsModal(false)}
                className="btn-ghost btn-icon btn-sm"
                disabled={!!importingMeeting}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="modal-body">
              {loadingMeetings ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="flex items-center gap-4 p-4 bg-slate-800/50 rounded-lg">
                      <div className="skeleton w-10 h-10 rounded-lg" />
                      <div className="flex-1">
                        <div className="skeleton h-4 w-48 mb-2" />
                        <div className="skeleton h-3 w-32" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : teamsMeetings.length === 0 ? (
                <div className="text-center py-8">
                  <Video className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                  <h4 className="text-slate-300 font-medium mb-1">No meetings found</h4>
                  <p className="text-sm text-slate-500">
                    No Teams meetings with transcripts found in the last 30 days
                  </p>
                </div>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {teamsMeetings.map((meeting) => (
                    <div
                      key={meeting.id}
                      className="flex items-center gap-4 p-4 bg-slate-800/50 rounded-lg hover:bg-slate-800 transition-colors"
                    >
                      <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Video className="w-5 h-5 text-blue-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-200 truncate">{meeting.subject}</p>
                        <div className="flex items-center gap-4 mt-1 text-sm text-slate-400">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatDateTime(meeting.start)}
                          </span>
                          {meeting.organizer && (
                            <span className="flex items-center gap-1">
                              <Users className="w-3 h-3" />
                              {meeting.organizer}
                            </span>
                          )}
                          {meeting.attendee_count > 0 && (
                            <span className="text-slate-500">
                              {meeting.attendee_count} attendee
                              {meeting.attendee_count !== 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => handleImportTeamsTranscript(meeting)}
                        disabled={!!importingMeeting}
                        className="btn-primary btn-sm flex-shrink-0"
                      >
                        {importingMeeting === meeting.id ? (
                          <>
                            <div className="spinner" />
                            Importing...
                          </>
                        ) : (
                          'Import'
                        )}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                onClick={() => setShowTeamsModal(false)}
                className="btn-secondary"
                disabled={!!importingMeeting}
              >
                Cancel
              </button>
              <button
                onClick={fetchTeamsMeetings}
                className="btn-ghost"
                disabled={loadingMeetings || !!importingMeeting}
              >
                <RefreshCw className={`w-4 h-4 ${loadingMeetings ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
